/**
 * Multi-burst budget allocator.
 *
 * Bursts share one grain ring and one fines ring. Near capacity, NEW burst
 * allocations scale down proportionally. Live particles are never evicted
 * to make room; we never write more slots than remain.
 */

export interface BurstBudgetRequest {
  readonly grains: number;
  readonly fines: number;
}

export interface BurstBudgetShare {
  readonly grains: number;
  readonly fines: number;
}

export interface PoolOccupancy {
  readonly live: number;
  readonly pending: number;
  readonly capacity: number;
}

export function poolUsed(occ: PoolOccupancy): number {
  return Math.max(0, occ.live) + Math.max(0, occ.pending);
}

export function poolRemaining(occ: PoolOccupancy): number {
  return Math.max(0, occ.capacity - poolUsed(occ));
}

/**
 * Room offered to a NEW burst. While anything is unexpired we hold back
 * 1/16 of the ring (or the last slot) so a follow-up click is not scaled
 * to 0 before the first window expires. Once only that reserve is left
 * it is given out. True 0 only when the ring is full.
 */
export function applyBurstReserve(free: number, capacity: number, occupied: number): number {
  const rem = Math.max(0, free | 0);
  if (rem <= 0) return 0;
  if (occupied <= 0 || capacity < 256) return rem;
  const reserve = Math.min(rem, Math.max(1, capacity >> 4));
  return rem <= reserve ? rem : rem - reserve;
}

export function poolBurstRoom(occ: PoolOccupancy): number {
  return applyBurstReserve(poolRemaining(occ), occ.capacity, poolUsed(occ));
}

/** Occupancy fraction in 0..1. Idle (nothing live or pending) is 0. */
export function budgetPressure(grain: PoolOccupancy, fine: PoolOccupancy): number {
  const g = grain.capacity > 0 ? poolUsed(grain) / grain.capacity : 0;
  const f = fine.capacity > 0 ? poolUsed(fine) / fine.capacity : 0;
  return Math.min(1, Math.max(0, Math.max(g, f)));
}

/**
 * Share `remaining` slots across N requested sizes.
 *
 * - Sum of allocations ≤ remaining.
 * - If sum(requested) ≤ remaining, each request is granted in full.
 * - Otherwise scale proportionally.
 * - No request that asked for > 0 is starved to 0 until remaining is
 *   truly exhausted (remaining < number of positive requests).
 */
export function allocateBurstBudgets(
  requested: readonly number[],
  remaining: number,
): number[] {
  const n = requested.length;
  const out = new Array<number>(n);
  const req = new Array<number>(n);
  let sum = 0;
  let positive = 0;
  for (let i = 0; i < n; i += 1) {
    const r = Math.max(0, Math.floor(requested[i]));
    req[i] = r;
    out[i] = 0;
    sum += r;
    if (r > 0) positive += 1;
  }
  const room = Math.max(0, Math.floor(remaining));
  if (n === 0 || room === 0 || sum === 0) return out;
  if (sum <= room) {
    for (let i = 0; i < n; i += 1) out[i] = req[i];
    return out;
  }

  if (room < positive) {
    let left = room;
    for (let i = 0; i < n && left > 0; i += 1) {
      if (req[i] > 0) {
        out[i] = 1;
        left -= 1;
      }
    }
    return out;
  }

  let allocated = 0;
  for (let i = 0; i < n; i += 1) {
    if (req[i] <= 0) continue;
    const share = Math.max(1, Math.floor((req[i] / sum) * room));
    out[i] = share;
    allocated += share;
  }

  if (allocated > room) {
    // max(1, floor) can overshoot — peel from the largest shares.
    while (allocated > room) {
      let best = -1;
      let bestVal = 1;
      for (let i = 0; i < n; i += 1) {
        if (out[i] > bestVal) {
          bestVal = out[i];
          best = i;
        }
      }
      if (best < 0) break;
      out[best] -= 1;
      allocated -= 1;
    }
  } else if (allocated < room) {
    let leftover = room - allocated;
    let guard = n * 2 + leftover;
    let i = 0;
    while (leftover > 0 && guard > 0) {
      if (req[i] > 0 && out[i] < req[i]) {
        out[i] += 1;
        leftover -= 1;
      }
      i = (i + 1) % n;
      guard -= 1;
    }
  }

  return out;
}

export function allocateBurstShares(
  requests: readonly BurstBudgetRequest[],
  grainRemaining: number,
  fineRemaining: number,
): BurstBudgetShare[] {
  const grains = allocateBurstBudgets(requests.map((r) => r.grains), grainRemaining);
  const fines = allocateBurstBudgets(requests.map((r) => r.fines), fineRemaining);
  return requests.map((_, i) => ({ grains: grains[i], fines: fines[i] }));
}
